import { useSearchParams } from 'react-router-dom';
import type { ChinaPaymentType } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';

import { PageTitle } from '@/components/ui.js';
import { MYPOZ_SITE_URL } from '@/lib/legacy-api.js';
import { NewRequestCard } from '@/features/china-payment/components/NewRequestCard.js';
import { PayToCard } from '@/features/china-payment/components/PayToCard.js';
import { ProcessingTimesCard } from '@/features/china-payment/components/ProcessingTimesCard.js';
import { RateCard } from '@/features/china-payment/components/RateCard.js';
import { usePayToAccounts } from '@/features/china-payment/usePayToAccounts.js';
import { useRateCard } from '@/features/china-payment/useRateCard.js';
import {
  CHINA_PAYMENT_TYPES as TYPES,
  DEFAULT_CHINA_PAYMENT_TYPE,
} from '@/features/china-payment/types.js';

/**
 * The China Payment page (MYP-10): one page with a tab per request type — the mentor's design.
 * It replaced the three pages that mirrored the old website's 充值中心 / 代付中心 / 转账中心; their
 * addresses redirect to the matching tab.
 *
 * The tab lives in the address (`?type=proxy`), so a link or a reload opens the same one; an
 * unknown value falls back to the first tab. The rate card and the accounts to pay into are the
 * same for every tab; the processing-times line and the old-site link follow it.
 *
 * MYP-59 brings the rate card, MYP-60 the live quote, MYP-61 the accounts to pay into, MYP-62 and
 * MYP-109 the request itself: three steps, sent for staff to process by hand. Which types can be
 * sent from here, and up to what amount, is `config.ts`; the rest link to the old website.
 */
export function ChinaPayment() {
  const { t } = useTranslation();
  const { t: tNav } = useTranslation('nav');
  const rates = useRateCard();
  const payTo = usePayToAccounts();
  const [searchParams, setSearchParams] = useSearchParams();

  const current =
    TYPES.find((entry) => entry.slug === searchParams.get('type')) ?? DEFAULT_CHINA_PAYMENT_TYPE;
  const selectType = (type: ChinaPaymentType) => {
    const next = TYPES.find((entry) => entry.type === type);
    if (!next) return;
    // Replaced, not pushed: switching tab is not a page the Back button should step through.
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set('type', next.slug);
        return params;
      },
      { replace: true },
    );
  };

  return (
    <>
      <PageTitle title={tNav('chinaPaymentCentre')} subtitle={t('chinaPayment.intro')} />

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <NewRequestCard
          types={TYPES.map((entry) => entry.type)}
          type={current.type}
          onTypeChange={selectType}
          oldSiteUrl={`${MYPOZ_SITE_URL}${current.oldSitePath}`}
          accounts={payTo.accounts}
          accountsStatus={payTo.status}
          onRetryAccounts={payTo.retry}
          {...(rates.card?.fees.topUp ? { topUpFee: rates.card.fees.topUp } : {})}
        />
        <div className="grid gap-5">
          <RateCard status={rates.status} card={rates.card} onRetry={rates.retry} />
          <PayToCard status={payTo.status} accounts={payTo.accounts} onRetry={payTo.retry} />
          <ProcessingTimesCard sameDayFromRmbCents={current.sameDayFromRmbCents} />
        </div>
      </div>
    </>
  );
}
